import type {Container} from './container.ts';
import type {Fetcher} from './fetcher/fetcher';

type FetchRetryerOptions = Partial<{
  fetchOptions: RequestInit;
  fetchImmediately: boolean;
  retryAfterResponseStatus: number;
  pollingBackoffMilliseconds: number;
  maxPollingJitterMilliseconds: number;
  maxPollingIntervalMilliseconds: number;
  giveUpAfterMinutes: number;
  skipCache: boolean;
}>;

/**
 * Similar to an invocation of Fetcher.fetch(), but retries the fetch() call on
 * failure.  The `.promise` property only settles when the fetch is eventually
 * successful, possibly after retries, or when retrying aborts.
 *
 * This doesn't implement the Fetcher interface because each instance only works
 * for a single URL. Changing the URL after fetching begins is not supported. If
 * you need to change the URL, create a new instance.  This class also
 * implements other methods like `hintReadyToFetch()` that Fetchers don't.
 *
 * You should only use this for cacheable requests like GET and HEAD, or for
 * PUT, PATCH, and DELETE requests that are idempotent.  Once a successful
 * response is received, the result is cached.
 *
 * Promise settling:
 *
 * - resolved: when the fetch completes successfully
 * - rejected: when the fetch is aborted, and retrying will no longer be
 *     attempted.  `.response` is set to the Response if the response's status
 *     caused the abort, or `undefined` otherwise,  `.errorDetails` is set to
 *     the string `"timeout"` in the case of a timeout (i.e. giving up) or
 *     `undefined` otherwise.
 *
 * The Promise always fulfills or rejects to itself, the FetchRetryer instance,
 * to make it easier for callers to use multiple instances.
 *
 * Exponential back-off is used for retries.  This means that the amount of time
 * between fetch attempts in number of units is: 1, 2, 4, 8, ...  The duration
 * of a unit is configurable.
 *
 * Random jitter is used so that multiple instances don't retry in lockstep.
 */
export class FetchRetryer {
  isFetching: boolean;
  isLoaded: boolean;
  isRetryingAborted: boolean;
  nonce: object | null;
  response: Response | null;
  errorDetails: string | null;
  numRetries: number;
  pollTimer: ReturnType<typeof setTimeout> | null;
  needsJitter: boolean;
  scheduleImmediateRetry: boolean;
  startDate: Date | null;
  url: RequestInfo;
  fetchOptions: RequestInit | undefined;
  fetchImmediately: boolean;
  retryAfterResponseStatus: number;
  pollingBackoffMilliseconds: number;
  maxPollingJitterMilliseconds: number;
  maxPollingIntervalMilliseconds: number;
  giveUpAfterMinutes: number;
  fetcher: Fetcher;
  _resolve: Function | null;  // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  _reject: Function | null;  // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  promise: Promise<FetchRetryer>;

  /**
   * Look up a FetchRetryer that's shared for equal URLs.
   *
   * Note that different options are not used to identify different
   * FetchRetryers.  They are only used the first time a FetchRetryer is created
   * for a URL.
   *
   * @param {Container} container The DI container.
   * @param {RequestInfo} urlOrRequest The URL to fetch, or a Request object.
   * @param {object | undefined} [options]
   * @param {RequestInit} [options.fetchOptions] The options to pass to fetch().
   * @param {boolean} [options.fetchImmediately=false] Whether to fetch
   *        immediately on instantation or URL change.  When false, it's up to
   *        the caller to call `hintReadyToFetch()`.
   * @param {number} [options.retryAfterResponseStatus=503] The HTTP status code
   *        to treat as a signal to retry the request.
   * @param {number} [options.pollingBackoffMilliseconds=1000] One unit of
   *        duration to increase the time between each fetch attempt.  We use
   *        exponential back-off, where the number of attempts is the exponent,
   *        and the result is multiplied by this unit.
   * @param {number} [options.maxPollingJitterMilliseconds=1000] Max amount to
   *        jitter the duration between fetch attempts.  This is so that many
   *        instances don't all try to fetch at the same time in lock-step.
   * @param {number} [options.maxPollingIntervalMilliseconds=10000] Max duration
   *        to wait between fetch attempts, before adding jitter.  Jitter needs
   *        to be added after so that many instances don't all get capped at the
   *        max.
   * @param {number} [options.giveUpAfterMinutes=2] If retrying continues for
   *        this many minutes, abort.
   * @param {boolean} [options.skipCache=false] Whether to bypass
   *        cached versions of the fetcher.
   * @return {FetchRetryer}
   */
  static for(container: Container, urlOrRequest: RequestInfo, options: FetchRetryerOptions = {}) {
    if (options && options.skipCache) {
      return new FetchRetryer(container, urlOrRequest, options);
    }

    /** @type {Map<RequestInfo, FetchRetryer>} */
    const fetchRetryerCache = container.cacheFor('FetchRetryer');
    const cached = fetchRetryerCache.get(urlOrRequest);
    if (cached !== undefined) {
      return cached;
    }

    const fetchRetryer = new FetchRetryer(container, urlOrRequest, options);
    fetchRetryerCache.set(urlOrRequest, fetchRetryer);

    return fetchRetryer;
  }

  /**
   * @param {Container} container The DI container.
   * @param {RequestInfo} urlOrRequest The URL to fetch, or a Request object.
   * @param {object} [options]
   * @param {RequestInit} [options.fetchOptions] The options to pass to fetch().
   * @param {fetchImmediately} [options.fetchImmediately=false] Whether to fetch
   *        immediately on instantation or URL change.  When false, it's up to
   *        the caller to call `hintReadyToFetch()`.
   * @param {number} [options.retryAfterResponseStatus=503] The HTTP status code
   *        to treat as a signal to retry the request.
   * @param {number} [options.pollingBackoffMilliseconds=250] One unit of
   *        duration to increase the time between each fetch attempt.  We use
   *        exponential back-off, where the number of attempts is the exponent,
   *        and the result is multiplied by this unit.
   * @param {number} [options.maxPollingJitterMilliseconds=100] Max amount to
   *        jitter the duration between fetch attempts.  This is so that many
   *        instances don't all try to fetch at the same time in lock-step.
   * @param {number} [options.maxPollingIntervalMilliseconds=10000] Max duration
   *        to wait between fetch attempts, before adding jitter.  Jitter needs
   *        to be added after so that many instances don't all get capped at the
   *        max.
   * @param {number} [options.giveUpAfterMinutes=2] If retrying continues for
   *        this many minutes, abort.
   */
  constructor(container: Container,
    urlOrRequest: RequestInfo,
    {
      fetchOptions = undefined,
      fetchImmediately = false,
      retryAfterResponseStatus = 503,
      pollingBackoffMilliseconds = 250,
      maxPollingJitterMilliseconds = 100,
      maxPollingIntervalMilliseconds = 10000,
      giveUpAfterMinutes = 2,
    }: FetchRetryerOptions = {},
  ) {
    // Caller-controlled parameters.
    this.fetcher = container.lookup('fetcher');
    this.url = urlOrRequest;
    this.fetchOptions = fetchOptions;
    this.fetchImmediately = fetchImmediately;
    this.retryAfterResponseStatus = retryAfterResponseStatus;
    this.pollingBackoffMilliseconds = pollingBackoffMilliseconds;
    this.maxPollingJitterMilliseconds = maxPollingJitterMilliseconds;
    this.maxPollingIntervalMilliseconds = maxPollingIntervalMilliseconds;
    this.giveUpAfterMinutes = giveUpAfterMinutes;

    // Internal state.
    this.isFetching = false;
    this.isLoaded = false;
    this.isRetryingAborted = false;
    this.nonce = null;
    this.response = null;
    this.errorDetails = null;
    this.numRetries = 0;
    this.pollTimer = null;
    this.needsJitter = true;
    this.scheduleImmediateRetry = false;
    this.startDate = null;

    // Use underscores to avoid confusion with a Promise.
    this._resolve = null;
    this._reject = null;

    /**
     * The one Promise that will be settled over all retries.  Callers should
     * attach listeners to this.
     */
    this.promise = new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
    });

    if (this.fetchImmediately) {
      this.startFetching();
    }
  }

  /**
   * @return {boolean}
   */
  get isLoading() {
    return Boolean(this.url) && !this.isLoaded && !this.isRetryingAborted;
  }

  /**
   * @return {boolean}
   */
  get hasStartedLoading() {
    return Boolean(this.startDate) || this.isLoaded || this.isRetryingAborted;
  }

  /**
   * Begin fetching the URL if we haven't already begun.
   *
   * When FetchRetryer instances are shared, you should prefer this over calling
   * `hintReadyToFetch()` since the hint will force an immediate retry when
   * called multiple times in quick succession.
   */
  startFetching() {
    if (!this.hasStartedLoading) {
      this.attemptFetch();
    }
  }

  /**
   * Force the promise to resolve.  You should only use this if you don't intend
   * to do any fetching.
   */
  resolve() {
    if (this._resolve) {
      this._resolve(this);
    }
  }

  abort() {
    if (this.isRetryingAborted) return;
    if (this.isLoaded) {
      throw new Error("Can't abort since already loaded");
    }
    this.isRetryingAborted = true;

    // If we're currently fetching, abandon the result.
    this.nonce = null;
    this.isFetching = false;

    // TODO: use an AbortController to abort the fetch.  That may actually be
    // difficult since a CachingFetcher may share the request with other
    // callers.

    // Cancel any existing timer.
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }

    this._reject(this);
    // Clean up.
    this._resolve = this._reject = null;
  }

  /**
   * Attempt to fetch the URL now.  You shouldn't normally need to call this.
   *
   * @return {Promise<void>} Promise that will resolve or reject when zero or
   * one attempt is finished, not when a subsequent retry finishes successfully.
   */
  async attemptFetch() {
    if (this.isFetching || this.isLoaded) return;

    // If this was called manually and a fetch has been scheduled, cancel it.
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }

    const url = this.url;
    if (!url) return;

    this.isFetching = true;

    if (!this.startDate) {
      this.startDate = new Date();
    }

    // Create a unique object.  It's set on the component so that we know we're
    // still waiting on this request, so it's crucial that this is done
    // **before** any async calls.
    const nonce = {};
    this.nonce = nonce;
    // eslint-disable-next-line no-console
    console.assert(nonce === this.nonce, 'FetchRetryer nonce equality failed');

    let hadErrorMakingRequest = false;
    let response: Response | null = null;
    let emitError = false;
    const handleFailedLoad = () => {
      // Make sure the component is waiting for this request.
      if (this.nonce === nonce) {
        this.nonce = null;
        this.isFetching = false;
        this.scheduleFetch(hadErrorMakingRequest);
        if (emitError && this._reject) {
          this.response = response;
          this.errorDetails = null;
          this._reject(this);
          // Clean up.
          this._resolve = this._reject = null;
        }
      }
    };

    try {
      // Make the request.
      response = await this.fetcher.fetch(url, this.fetchOptions);
    } catch {
      hadErrorMakingRequest = true;
    }
    if (response && response.status !== this.retryAfterResponseStatus &&
        this.nonce === nonce) {
      this.isRetryingAborted = true;
      emitError = true;
    }
    if (!response?.ok) {
      handleFailedLoad();
      return;
    }

    // Make sure the component is waiting for this request.
    const sameRequest = this.nonce === nonce;
    if (sameRequest) {
      // Cancel an immediate retry if one was scheduled.
      this.scheduleImmediateRetry = false;
      // Clean up.
      this.nonce = null;
      this.isFetching = false;

      if (this._resolve) {
        this.isLoaded = true;
        // Save the result.
        this.response = response;
        // Actually loaded successfully.
        this._resolve(this);
        // Clean up.
        this._resolve = this._reject = null;
      }
    }
  }

  /**
   * Call this when the URL is likely to be ready to be fetched.  We don't
   * assume that the caller is reliable.
   */
  hintReadyToFetch() {
    // If we're fully loaded or aborted retrying, don't do anything.
    if (this.isLoaded || this.isRetryingAborted) return;

    // Multiple instances are probably hinted at the same time, so we need
    // jitter again.
    this.needsJitter = true;

    if (this.isFetching) {
      this.scheduleImmediateRetry = true;
    } else {
      this.attemptFetch();
    }
  }

  /**
   * @private
   */
  scheduleFetch(suppressTimeoutIncrease = false) {
    // If a fetch is already scheduled, there's nothing to do.
    if (this.pollTimer) return;
    if (this.isRetryingAborted) return;

    let isImmediateRetry = false;
    let timeout;
    let jitter;
    if (this.scheduleImmediateRetry) {
      this.scheduleImmediateRetry = false;
      isImmediateRetry = true;
      timeout = 0;
      jitter = 0;
    } else {
      // Exponential back-off.
      timeout = 2 ** this.numRetries * this.pollingBackoffMilliseconds;

      timeout = Math.min(timeout, this.maxPollingIntervalMilliseconds);

      if (this.needsJitter) {
        jitter = this.maxPollingJitterMilliseconds * Math.random();
        // Once we add jitter, it staggers this instance from other instances
        // in a uniform distribution.  If we kept adding jitter, we would
        // create a normal distribution instead.
        this.needsJitter = false;
      } else {
        jitter = 0;
      }
    }

    let millisToWait = timeout + jitter;
    const giveUpTime = this.giveUpTime();
    if (giveUpTime) {
      const now = new Date();
      // Make sure we abort at the give-up time, not later.
      millisToWait = Math.min(giveUpTime.getTime() - now.getTime(), millisToWait);
      // Don't allow this to go negative if we've already passed the give-up
      // time.
      millisToWait = Math.max(0, millisToWait);
    }

    // If the caller asked for an immediate retry, don't count that against
    // normal retries that increase the back-off time.
    if (!isImmediateRetry && !suppressTimeoutIncrease &&
        this.numRetries < Number.MAX_SAFE_INTEGER) {
      this.numRetries++;
    }

    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;

      // Check if we've reached the give-up time.
      const giveUpTime = this.giveUpTime();
      if (giveUpTime) {
        const now = new Date();
        if (now >= giveUpTime) {
          this.giveUp();
          return;
        }
      }

      this.attemptFetch();
    }, millisToWait);
  }

  /**
   * Return the time at which we should give up on fetching and abort.
   * @private
   * @return {Date | null}
   */
  giveUpTime() {
    if (!this.startDate) return null;
    return new Date(this.startDate.getTime() + this.giveUpAfterMinutes * 60000);
  }

  /**
   * Abort due to timeout.
   * @private
   */
  giveUp() {
    if (this._reject) {
      this.isRetryingAborted = true;
      this.errorDetails = 'timeout';
      this._reject(this);
      // Clean up.
      this._resolve = this._reject = null;
    }
  }
}
